import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PenToolIcon as ToolIcon, Gift, Box, Sun, Moon, MessageCircle, Star, Crown, Heart, ShoppingBag, DollarSign, Camera } from 'lucide-react';
import { motion } from 'framer-motion';
import { useApp } from '../context/AppContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tokenBalance, user } = useApp();
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  // Hide navigation on the Whop page
  const isWhopPage = location.pathname === '/whop';
  if (isWhopPage) {
    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-br from-orange-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm shadow-sm">
          <div className="container mx-auto px-4 py-3">
            <div className="flex justify-between items-center">
              <motion.button 
                onClick={() => navigate('/')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center space-x-3"
              >
                <motion.div 
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                  className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center shadow-lg"
                >
                  <Camera size={24} className="text-yellow-300" />
                </motion.div>
                <span className="text-2xl font-bold bg-gradient-to-r from-orange-500 via-purple-500 to-blue-500 text-transparent bg-clip-text animate-gradient">
                  HiTryAi
                </span>
              </motion.button>
              
              <div className="flex items-center space-x-4">
                <motion.button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                </motion.button>

                <motion.button
                  onClick={() => navigate('/')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-4 py-2 rounded-lg"
                >
                  <Home size={18} />
                  <span className="hidden sm:inline ml-2">Back to Home</span>
                </motion.button>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-grow container mx-auto px-4 py-6 mt-16">
          {children}
        </main>
      </div>
    );
  }

  const navItems = [
    { path: '/', icon: <Crown size={24} className="text-yellow-500" />, label: 'For Creators' },
    { path: '/earn', icon: <DollarSign size={24} className="text-green-500" />, label: 'Earn' },
    { path: '/community', icon: <MessageCircle size={24} className="text-indigo-500" />, label: 'Community' }
  ];

  if (user?.is_admin) {
    navItems.push({ path: '/admin', icon: <Star size={24} />, label: 'Admin' });
  }

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-orange-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <motion.button 
              onClick={() => navigate('/')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center space-x-3"
            >
              <span className="text-2xl font-bold bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 text-transparent bg-clip-text animate-gradient">
                Hi, Try Ai
              </span>
            </motion.button>
            
            <div className="flex items-center space-x-4">
              <motion.button
                onClick={() => setIsDarkMode(!isDarkMode)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center space-x-1 bg-gradient-to-r from-orange-100 to-blue-100 dark:from-orange-900/30 dark:to-blue-900/30 rounded-full px-3 py-1 cursor-pointer hover:bg-gradient-to-r hover:from-orange-200 hover:to-blue-200 dark:hover:from-orange-900/40 dark:hover:to-blue-900/40 transition-colors"
              >
                <Gift size={16} className="text-orange-500 dark:text-orange-400" />
                <span className="font-medium">{tokenBalance}</span>
              </motion.button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-grow container mx-auto px-4 py-6 mt-16">
        {children}
      </main>

      <nav className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 shadow-lg border-t border-gray-200 dark:border-gray-700">
        <div className="flex justify-around max-w-screen-xl mx-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center pt-2 pb-1 px-1 ${
                  isActive 
                    ? item.path === '/community'
                      ? 'text-indigo-500'
                      : item.path === '/health'
                        ? 'text-red-500'
                        : item.path === '/earn'
                             ? 'text-green-500'
                             : item.path === '/'
                               ? 'text-yellow-400'
                               : 'text-blue-500' 
                    : item.path === '/community'
                      ? 'text-indigo-500'
                        : item.path === '/earn'
                             ? 'text-green-500'
                             : item.path === '/'
                               ? 'text-yellow-400'
                               : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                <motion.div
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  className="w-6 h-6 mb-1 flex items-center justify-center"
                >
                  {item.icon}
                </motion.div>
                <span className="text-xs font-medium">{item.label}</span>
                {isActive && (
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: '3rem' }}
                    className={`h-1 mt-1 rounded-t-full ${
                      item.path === '/community'
                        ? 'bg-gradient-to-r from-indigo-500 to-purple-500'
                        : item.path === '/health'
                          ? 'bg-gradient-to-r from-red-500 to-pink-500'
                          : item.path === '/earn'
                               ? 'bg-gradient-to-r from-green-500 to-green-400'
                               : item.path === '/'
                                 ? 'bg-gradient-to-r from-yellow-400 to-orange-500'
                                 : 'bg-gradient-to-r from-orange-500 to-blue-500'
                    }`}
                  />
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default Layout;